import React, { useMemo, useState } from 'react'
import { useQueryClient } from 'react-query'
import useReduxStore from '../../hooks/useReduxStore'
import {
  AI_COST_QUERY_KEYS,
  syncDeepgram,
  useAICostSummary,
  useAICostDaily,
  useAICostProviders,
  useAICostModels,
  useAICostFeatures,
  useAICostLogs,
  useAICostFilters,
  useGeminiUsage,
} from '../../repository/aiCostApi'
import AIFilters, { defaultFilters, DASHBOARD_TIMEZONE } from '../../components/AICost/AIFilters'
import AISummaryCards from '../../components/AICost/AISummaryCards'
import AICostTrendChart from '../../components/AICost/AICostTrendChart'
import AIProviderChart from '../../components/AICost/AIProviderChart'
import AIModelBreakdown from '../../components/AICost/AIModelBreakdown'
import AIFeatureBreakdown from '../../components/AICost/AIFeatureBreakdown'
import AIUsageTrendChart from '../../components/AICost/AIUsageTrendChart'
import AIUsageTable from '../../components/AICost/AIUsageTable'
import GeminiUsageSection from '../../components/AICost/GeminiUsageSection'
import '../../components/AICost/aiCost.css'

const AIUsageDashboard = () => {
  const queryClient = useQueryClient()
  const { selector: user } = useReduxStore('auth')
  const canEditProject = !!user?.userInfo?.permission?.project?.edit?.value

  const [filters, setFilters] = useState(defaultFilters)
  const [sort, setSort] = useState({ sortBy: 'date', sortOrder: 'desc' })
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(20)
  const [sync, setSync] = useState({ running: false, message: null, isError: false })

  // Any filter change goes back to page 1
  const changeFilters = (next) => {
    setFilters(next)
    setPage(1)
  }

  // timezone: backend buckets days and reads startDate/endDate in it (its default is IST)
  const params = useMemo(() => ({ ...filters, timezone: DASHBOARD_TIMEZONE }), [filters])
  const logParams = useMemo(() => ({ ...params, ...sort, page, limit }), [params, sort, page, limit])

  const summary = useAICostSummary(params)
  const daily = useAICostDaily(params)
  const providers = useAICostProviders(params)
  const models = useAICostModels(params)
  const features = useAICostFeatures(params)
  const logs = useAICostLogs(logParams)
  const options = useAICostFilters({})
  // Gemini-only section: hidden while another provider is filtered
  const showGemini = !filters.provider || filters.provider === 'gemini'
  const gemini = useGeminiUsage(params, showGemini)

  const handleSync = async () => {
    setSync({ running: true, message: null, isError: false })
    try {
      // No projectId = backend syncs every project with a Deepgram key
      const res = await syncDeepgram(filters.projectId ? { projectId: filters.projectId } : {})
      const stats = res.data?.stats
      const scope = stats?.projects ? ` across ${stats.projects} projects` : ''
      setSync({ running: false, isError: false, message: stats ? `Synced ${stats.requests} requests from Deepgram${scope}` : res.message })
    } catch (err) {
      setSync({ running: false, isError: true, message: err.message })
    }
    AI_COST_QUERY_KEYS.forEach((key) => queryClient.invalidateQueries(key))
  }

  return (
    <div className="ai-cost container-fluid py-3">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div>
          <h4 className="m-0">AI Usage & Cost</h4>
          <div className="text-muted small">Deepgram usage by project, model and feature</div>
        </div>
        {canEditProject && (
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            disabled={sync.running}
            title={filters.projectId ? 'Pull the latest Deepgram data now' : 'Pull the latest Deepgram data for all projects'}
            onClick={handleSync}>
            {sync.running ? 'Syncing Deepgram...' : 'Sync Deepgram now'}
          </button>
        )}
      </div>

      {sync.message && <div className={`alert ${sync.isError ? 'alert-danger' : 'alert-success'} py-2`}>{sync.message}</div>}

      <AIFilters filters={filters} onChange={changeFilters} options={options.data?.data} />

      {summary.error && <div className="alert alert-danger py-2">{summary.error.message}</div>}

      <AISummaryCards summary={summary.data?.data} isLoading={summary.isLoading} />

      <AICostTrendChart daily={daily.data?.data} isLoading={daily.isLoading} error={daily.error} />

      <div className="ai-cost-grid-3">
        <AIProviderChart providers={providers.data?.data} isLoading={providers.isLoading} error={providers.error} />
        <AIModelBreakdown models={models.data?.data} isLoading={models.isLoading} error={models.error} />
        <AIFeatureBreakdown features={features.data?.data} isLoading={features.isLoading} error={features.error} />
      </div>

      <AIUsageTrendChart daily={daily.data?.data} isLoading={daily.isLoading} error={daily.error} />

      {showGemini && <GeminiUsageSection usage={gemini.data?.data} isLoading={gemini.isLoading} error={gemini.error} />}

      <AIUsageTable
        data={logs.data?.data}
        isLoading={logs.isLoading}
        error={logs.error}
        sort={sort}
        onSort={(next) => {
          setSort(next)
          setPage(1)
        }}
        page={page}
        limit={limit}
        onPage={setPage}
        onLimit={(n) => {
          setLimit(n)
          setPage(1)
        }}
      />
    </div>
  )
}

export default AIUsageDashboard
